import actions from './actions.js'
import mutations from './mutations.js'
import jsCookie from 'js-cookie'
import Roles from './Roles'

export const DEFAULT_INDEX_OBJECT = {
  /**
   * oidcAuth {redirectUser...}
   *    - redirectUser (bool) - if set to true, this will redirect the user
   */
  oidcAuth: null,
  username: null,
  token: null,
  error: null,
  loading: false,
  refreshToken: null,
  expires: null,
  loggingOut: false,
  userPermissions: [],
  roles: new Roles(),
}

function state() {
  const cookie = jsCookie.get('auth')
  let credentials = {}
  if (cookie) {
    credentials = JSON.parse(cookie)
    // eslint-disable-next-line no-undef
    adobeAnalytics.login(credentials.username)
  }
  // default values go here
  return {
    ...DEFAULT_INDEX_OBJECT,
    ...credentials,
  }
}

const getters = {
  username: state => (state.username ? state.username.toLowerCase() : state.username),
  userPermissions(state) {
    return state.userPermissions
  },
  userRoles: state => state.roles,
  hasPublishPermissions(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.some(p =>
      [
        PERMISSIONS.ADSTRA_PUBLISH,
        PERMISSIONS.AMRLD_PUBLISH,
        PERMISSIONS.BYOD_PUBLISH,
        PERMISSIONS.COMSCORE_PUBLISH,
        PERMISSIONS.COMSCORE_TV_PUBLISH,
        PERMISSIONS.DNB_PUBLISH,
        PERMISSIONS.MAX_PUBLISH,
        PERMISSIONS.MRI_PUBLISH,
        PERMISSIONS.NCS_PUBLISH,
        PERMISSIONS.NPOWER_PUBLISH,
        PERMISSIONS.PID_PUBLISH,
        PERMISSIONS.PRISM_PUBLISH,
        PERMISSIONS.VIDEOAMP_PUBLISH,
        PERMISSIONS.TVE_PUBLISH,
        PERMISSIONS.EPSILON_PUBLISH,
        PERMISSIONS.DISCOVERY_PLUS_PUBLISH,
        PERMISSIONS.DCR_PUBLISH,
        PERMISSIONS.LIVERAMP_PUBLISH,
        PERMISSIONS.WBGAMES_PUBLISH,
        PERMISSIONS.RETARGETING_PUBLISH,
        PERMISSIONS.FREEWHEEL_PUBLISH,
        PERMISSIONS.GOOGLE_AD_MANAGER_PUBLISH,
        PERMISSIONS.LAL_PUBLISH,
      ].includes(p),
    )
  },
  hasArchivePermissions(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.some(p =>
      [
        PERMISSIONS.ADSTRA_ARCHIVE,
        PERMISSIONS.AMRLD_ARCHIVE,
        PERMISSIONS.BYOD_ARCHIVE,
        PERMISSIONS.COMSCORE_ARCHIVE,
        PERMISSIONS.COMSCORE_TV_ARCHIVE,
        PERMISSIONS.DNB_ARCHIVE,
        PERMISSIONS.MAX_ARCHIVE,
        PERMISSIONS.MRI_ARCHIVE,
        PERMISSIONS.NCS_ARCHIVE,
        PERMISSIONS.NPOWER_ARCHIVE,
        PERMISSIONS.PID_ARCHIVE,
        PERMISSIONS.PRISM_ARCHIVE,
        PERMISSIONS.VIDEOAMP_ARCHIVE,
        PERMISSIONS.TVE_ARCHIVE,
        PERMISSIONS.EPSILON_ARCHIVE,
        PERMISSIONS.DISCOVERY_PLUS_ARCHIVE,
        PERMISSIONS.DCR_ARCHIVE,
        PERMISSIONS.LIVERAMP_ARCHIVE,
        PERMISSIONS.WBGAMES_ARCHIVE,
        PERMISSIONS.RETARGETING_ARCHIVE,
        PERMISSIONS.FREEWHEEL_ARCHIVE,
        PERMISSIONS.GOOGLE_AD_MANAGER_ARCHIVE,
        PERMISSIONS.LAL_ARCHIVE,
      ].includes(p),
    )
  },
  hasCreateEditPermissions(state, rootState, rootGetters) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.some(p =>
      [
        PERMISSIONS.ADSTRA_CREATE_EDIT,
        PERMISSIONS.AMRLD_CREATE_EDIT,
        PERMISSIONS.BYOD_CREATE_EDIT,
        PERMISSIONS.COMSCORE_CREATE_EDIT,
        PERMISSIONS.COMSCORE_TV_CREATE_EDIT,
        PERMISSIONS.DNB_CREATE_EDIT,
        PERMISSIONS.MAX_CREATE_EDIT,
        PERMISSIONS.MRI_CREATE_EDIT,
        PERMISSIONS.NCS_CREATE_EDIT,
        PERMISSIONS.NPOWER_CREATE_EDIT,
        PERMISSIONS.PID_CREATE_EDIT,
        PERMISSIONS.PRISM_CREATE_EDIT,
        PERMISSIONS.VIDEOAMP_CREATE_EDIT,
        PERMISSIONS.TVE_CREATE_EDIT,
        PERMISSIONS.EPSILON_CREATE_EDIT,
        PERMISSIONS.DISCOVERY_PLUS_CREATE_EDIT,
        PERMISSIONS.DCR_CREATE_EDIT,
        PERMISSIONS.LIVERAMP_CREATE_EDIT,
        PERMISSIONS.WBGAMES_CREATE_EDIT,
        PERMISSIONS.RETARGETING_CREATE_EDIT,
        PERMISSIONS.FREEWHEEL_CREATE_EDIT,
        PERMISSIONS.GOOGLE_AD_MANAGER_CREATE_EDIT,
        PERMISSIONS.LAL_CREATE_EDIT,
      ].includes(p),
    )
  },
  hasViewPermissions(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.some(p =>
      [
        PERMISSIONS.ADSTRA_VIEW,
        PERMISSIONS.AMRLD_VIEW,
        PERMISSIONS.BYOD_VIEW,
        PERMISSIONS.COMSCORE_VIEW,
        PERMISSIONS.COMSCORE_TV_VIEW,
        PERMISSIONS.DNB_VIEW,
        PERMISSIONS.MAX_VIEW,
        PERMISSIONS.MRI_VIEW,
        PERMISSIONS.NCS_VIEW,
        PERMISSIONS.NPOWER_VIEW,
        PERMISSIONS.PID_VIEW,
        PERMISSIONS.PRISM_VIEW,
        PERMISSIONS.VIDEOAMP_VIEW,
        PERMISSIONS.TVE_VIEW,
        PERMISSIONS.EPSILON_VIEW,
        PERMISSIONS.DISCOVERY_PLUS_VIEW,
        PERMISSIONS.DCR_VIEW,
        PERMISSIONS.LIVERAMP_VIEW,
        PERMISSIONS.WBGAMES_VIEW,
        PERMISSIONS.RETARGETING_VIEW,
        PERMISSIONS.FREEWHEEL_VIEW,
        PERMISSIONS.GOOGLE_AD_MANAGER_VIEW,
        PERMISSIONS.LAL_VIEW,
      ].includes(p),
    )
  },
  hasAdminPagePermission(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.includes(PERMISSIONS.ADMIN_VIEW)
  },
  hasAdminEditUserPermission(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.includes(PERMISSIONS.ADMIN_CREATE_EDIT_USERS)
  },
  hasAdminEditRolesPermission(state, rootState) {
    const PERMISSIONS = rootState.appPermissions || {}
    return state.userPermissions.includes(PERMISSIONS.ADMIN_CREATE_EDIT_ROLES)
  },
}

const module = {
  state,
  getters,
  mutations,
  actions,
}

export default module



// WEBPACK FOOTER //
// ./src/app/store/modules/auth/index.js